"use client";

import React from "react";
import Link from "next/link";
import { Sparkles, Calendar, ChevronRight, CheckCircle2, ArrowRight } from "lucide-react";
import { EVENT_DROPDOWN_ITEMS } from "./nav-data";

interface EventDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenOrderModal: (service?: string) => void;
}

export function EventDropdown({ isOpen, onClose, onOpenOrderModal }: EventDropdownProps) {
  const col1 = EVENT_DROPDOWN_ITEMS.slice(0, 6);
  const col2 = EVENT_DROPDOWN_ITEMS.slice(6);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    onClose();
    if (typeof window !== "undefined" && window.location.pathname === "/") {
      const orderSection = document.getElementById("order");
      if (orderSection) {
        e.preventDefault();
        orderSection.scrollIntoView({ behavior: "smooth" });
        window.history.pushState(null, "", href);
      }
    }
  };

  return (
    <div
      className={`absolute left-1/2 -translate-x-1/2 lg:left-64 lg:translate-x-0 top-full pt-2 z-50 transition-all duration-200 ease-out origin-top ${
        isOpen
          ? "opacity-100 translate-y-0 pointer-events-auto visible"
          : "opacity-0 -translate-y-2 pointer-events-none invisible"
      }`}
      role="region"
      aria-label="Меню Оформление мероприятий"
    >
      {/* Invisible bridge so mouse hover doesn't drop */}
      <div className="absolute -top-3 left-0 right-0 h-4 bg-transparent" />

      {/* Main Dropdown Card */}
      <div className="w-[calc(100vw-32px)] sm:w-[780px] rounded-2xl border border-gray-200/90 dark:border-zinc-800/90 bg-white/98 dark:bg-[#0c0f18]/98 backdrop-blur-2xl shadow-xl shadow-slate-900/10 dark:shadow-black/60 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-12 p-6 gap-6">
          {/* Left: 2 Columns of Links (7 cols of 12) */}
          <div className="md:col-span-7 flex flex-col justify-between">
            <div>
              {/* Header */}
              <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100 dark:border-zinc-800/80">
                <Link
                  href="/event/"
                  onClick={(e) => handleLinkClick(e, "/event/")}
                  className="flex items-center gap-2 group"
                >
                  <div className="w-7 h-7 rounded-lg bg-pink-500/10 text-[#FF2E93] flex items-center justify-center">
                    <Calendar className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-sm font-bold text-gray-950 dark:text-white group-hover:text-[#FF2E93] transition-colors">
                    Оформление мероприятий
                  </span>
                </Link>
                {/* <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-gradient-to-r from-[#0a3d90]/10 to-[#55a9ef]/15 text-[#0a3d90] dark:text-[#55a9ef] font-bold">
                  Для бизнеса
                </span> */}
              </div>

              {/* 2 Sub-columns of links */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                <div className="space-y-0.5">
                  {col1.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={(e) => handleLinkClick(e, item.href)}
                      className="group flex items-center justify-between py-1.5 px-2 rounded-md text-[13px] text-gray-700 dark:text-zinc-300 hover:text-[#0a3d90] dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-zinc-800/60 transition-all duration-150"
                    >
                      <span className="transition-transform duration-150 group-hover:translate-x-0.5 line-clamp-1">
                        {item.title}
                      </span>
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#FF2E93]" />
                    </Link>
                  ))}
                </div>

                <div className="space-y-0.5">
                  {col2.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={(e) => handleLinkClick(e, item.href)}
                      className="group flex items-center justify-between py-1.5 px-2 rounded-md text-[13px] text-gray-700 dark:text-zinc-300 hover:text-[#0a3d90] dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-zinc-800/60 transition-all duration-150"
                    >
                      <span className="transition-transform duration-150 group-hover:translate-x-0.5 line-clamp-1">
                        {item.title}
                      </span>
                      <ChevronRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#FF2E93]" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-3 mt-3 border-t border-gray-100 dark:border-zinc-800 flex items-center justify-between">
              <Link
                href="/event/"
                onClick={(e) => handleLinkClick(e, "/event/")}
                className="text-xs font-semibold text-[#FF2E93] hover:underline flex items-center gap-1"
              >
                <span>Все форматы мероприятий</span>
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 rounded-xl border border-gray-200/80 dark:border-zinc-800/80 bg-gradient-to-b from-gray-50/90 to-blue-50/40 dark:from-zinc-900/90 dark:to-[#0a1a36]/40 p-4 sm:p-5 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#FF2E93]" />
                <span className="text-[11px] font-mono uppercase tracking-wider font-bold text-gray-700 dark:text-zinc-300">
                  Под индивидуальный расчет
                </span>
              </div>

              <h4 className="text-sm font-bold text-gray-950 dark:text-white mb-2 leading-snug">
                Комплексное оформление площадок
              </h4>

              <p className="text-xs text-gray-600 dark:text-zinc-400 mb-4 leading-relaxed">
                Выставки, форумы, корпоративы и презентации под ключ. От 3D-макета до ночного монтажа.
              </p>

              <div className="space-y-1.5 mb-5 text-[11px] text-gray-700 dark:text-zinc-300">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span>Расчет сметы за 2 часа</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span>Работа по договору с НДС</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span>Монтаж и демонтаж 24/7</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                onClose();
                onOpenOrderModal("Оформление мероприятий");
              }}
              className="w-full py-2 px-3 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-[#0a3d90] to-[#55a9ef] hover:from-[#083070] hover:to-[#4293d8] transition-all shadow-sm active:scale-[0.98] text-center"
            >
              Рассчитать смету
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
